import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import styles from '../../styles/GamesDataBrowser.module.css';

const VenuesDataBrowser = () => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    state: '',
    capacity_min: '',
    capacity_max: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  const fetchVenues = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });

      const response = await api.get(`/admin/venues?${params}`);
      setVenues(response.data.venues);
      setPagination(response.data.pagination);
      setError(null);
    } catch (err) {
      setError('Failed to fetch venues data');
      console.error('Error fetching venues:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    fetchVenues();
  }, [fetchVenues]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      state: '',
      capacity_min: '',
      capacity_max: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  if (loading) {
    return <div className={styles.loading}>Loading venues...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  return (
    <div className={styles.gamesDataBrowser}>
      <div className={styles.tabs}>
        <h3>Venues Data Browser</h3>
      </div>

      {/* Filters */}
      <div className={styles.weekSelector}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
          <div>
            <label>Search:</label>
            <input
              type="text"
              placeholder="Venue name or city..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              style={{ width: '100%', padding: '8px', backgroundColor: '#3a3a3a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label>State:</label>
            <input
              type="text"
              placeholder="State code..."
              value={filters.state}
              onChange={(e) => handleFilterChange('state', e.target.value)}
              style={{ width: '100%', padding: '8px', backgroundColor: '#3a3a3a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label>Min Capacity:</label>
            <input
              type="number"
              placeholder="Min capacity..."
              value={filters.capacity_min}
              onChange={(e) => handleFilterChange('capacity_min', e.target.value)}
              style={{ width: '100%', padding: '8px', backgroundColor: '#3a3a3a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label>Max Capacity:</label>
            <input
              type="number"
              placeholder="Max capacity..."
              value={filters.capacity_max}
              onChange={(e) => handleFilterChange('capacity_max', e.target.value)}
              style={{ width: '100%', padding: '8px', backgroundColor: '#3a3a3a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}
            />
          </div>
        </div>
        <button
          onClick={clearFilters}
          style={{ marginTop: '10px', padding: '8px 16px', backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          Clear Filters
        </button>
      </div>

      {/* Venues Table */}
      <div className={styles.lockTable}>
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>City</th>
              <th>State</th>
              <th>Capacity</th>
              <th>Grass</th>
              <th>Dome</th>
              <th>Timezone</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {venues.map(venue => (
              <tr key={venue.id}>
                <td>{venue.id}</td>
                <td>{venue.name}</td>
                <td>{venue.city}</td>
                <td>{venue.state}</td>
                <td>{venue.capacity?.toLocaleString() || '-'}</td>
                <td>{venue.grass ? 'Yes' : 'No'}</td>
                <td>{venue.dome ? 'Yes' : 'No'}</td>
                <td>{venue.timezone}</td>
                <td>{new Date(venue.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div style={{ marginTop: '20px', textAlign: 'center', color: '#fff' }}>
          <span>
            Page {pagination.page} of {pagination.totalPages}
            ({pagination.total} total venues)
          </span>
          <div style={{ marginTop: '10px' }}>
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              style={{
                margin: '0 5px',
                padding: '5px 10px',
                backgroundColor: pagination.page <= 1 ? '#555' : '#007bff',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              style={{
                margin: '0 5px',
                padding: '5px 10px',
                backgroundColor: pagination.page >= pagination.totalPages ? '#555' : '#007bff',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: pagination.page >= pagination.totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VenuesDataBrowser;
